import styled from 'styled-components'
import { useLocation } from 'react-router'

import { media } from 'styles/media'
import typeStyle from 'styles/typeStyle'
import { useGetMarketServiceRatesQuery } from 'hooks/useQueries'

import serviceRateToPercentage from '../../../components/utils/serviceRateToPercentage'
import { ShowMoreAtBreakpoint } from '../../../components/ShowMore'

import ButtonLink from '../../../components/display/ButtonLink'
import SimpleAnchor from '../../../components/display/SimpleAnchor'

import addMarketParamToSearch from './addMarketParamToSearch'
import { VisibilityHint } from './shared'

const Outer = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme['-ds-neutral-200'].value};
  padding: 60px 30px;

  ${media.md`
    flex-direction: row;
    padding: 60px;
  `}
`

const TitleWrapper = styled.div`
  max-width: 510px;

  p {
    color: ${({ theme }) => theme['-ds-core-300'].value};
  }

  h2 {
    ${typeStyle('-ds-font-title-1')}
    color: ${({ theme }) => theme['-ds-core-400'].value};
  }
`

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px 0;

  & > *:not(:last-child) {
    margin-bottom: 20px;
    margin-right: 0;
  }

  ${media.md`
    flex-direction: row;

    & > *:not(:last-child) {
      margin-right: 20px;
      margin-bottom: 0;
    }
  `}
`

const ContentWrapper = styled.div`
  max-width: 680px;
  color: ${({ theme }) => theme['-ds-core-300'].value};

  p,
  ul {
    font-size: ${({ theme }) => theme['-ds-font-size-sm']['font-size'].value};
    line-height: 1.8;
  }

  ul {
    margin: 0;
    padding: 0 0 0 18px;
  }
`

const Inner = styled.div`
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;

  ${media.md`
    flex-direction: row;
  `}
`

type BenefitsBannerProps = {
  listingId: string
  hostFirstName: string
  marketCode: GQL_MarketCode
}

const BenefitsBanner = ({
  listingId,
  hostFirstName,
  marketCode,
}: BenefitsBannerProps) => {
  const { search } = useLocation()

  const searchWithMarket = addMarketParamToSearch(search, marketCode)

  const { data } = useGetMarketServiceRatesQuery({
    variables: {
      code: marketCode,
    },
    skip: marketCode == null,
  })

  const hostServiceFeeBaseRate = serviceRateToPercentage(
    data?.market?.hostServiceFeeBaseRate,
  )
  const rateText = hostServiceFeeBaseRate
    ? ` at only ${hostServiceFeeBaseRate}`
    : ''

  return (
    <Outer>
      <Inner>
        <TitleWrapper>
          <p>Hey {hostFirstName},</p>
          <h2>You&apos;re invited to become a Riparide Host</h2>
        </TitleWrapper>
        <ContentWrapper>
          <p>
            We’ve created a draft listing to make things as easy as possible for
            you. But don’t worry, you can edit any of these details before
            publishing your listing on Riparide, at which time you’ll officially
            join our Host Community.
          </p>
          <p>Some of the perks of joining Riparide:</p>
          <ShowMoreAtBreakpoint
            equality="less_than"
            breakpoint="md"
            style={{ marginBottom: 30, padding: 0 }}
          >
            <ul>
              <li>
                We bring you the best customers: Riparide’s main customers are
                couples looking to connect with nature and relax.
              </li>
              <li>
                We offer the best host fee in the market
                {rateText}, putting more money back in your pocket.
              </li>
              <li>
                Our Host Support Team are here for you, providing highly
                personable service when you need it. We’re real humans, not
                bots!
              </li>
              <li>
                Riparide Stories showcase your property and provide you with
                quality promotional content right away.{' '}
                <SimpleAnchor href="/stories-hosts">Learn more.</SimpleAnchor>
              </li>
            </ul>

            <p style={{ marginTop: '8px' }}>
              Start by completing the listing details, it only takes a few
              minutes.
            </p>
          </ShowMoreAtBreakpoint>
          <CTAWrapper>
            <ButtonLink href={`/my/listings/${listingId}/edit`} rounded>
              Complete Details
            </ButtonLink>
            <ButtonLink
              href={`/onboarding${searchWithMarket}`}
              target="_blank"
              rel="noopener noreferrer"
              rounded
              ghost
            >
              Learn more about hosting
            </ButtonLink>
          </CTAWrapper>
          <VisibilityHint>This content is only visible to you</VisibilityHint>
        </ContentWrapper>
      </Inner>
    </Outer>
  )
}

export default BenefitsBanner
